/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { FormArray, FormControl, FormGroup, UntypedFormArray, UntypedFormGroup } from '@angular/forms';

export class FormUtils {
  static markAllAsTouched(form: UntypedFormGroup | FormGroup | UntypedFormArray | FormArray | FormControl) {
    if (form instanceof FormArray) {
      form.controls.forEach((control) => {
        if (control instanceof FormGroup || control instanceof FormArray) {
          this.markAllAsTouched(control);
        }
        control.markAsTouched();
      });
      return;
    } else if (form instanceof FormGroup) {
      Object.keys(form.controls).forEach((key) => {
        if (form.controls[key] instanceof FormGroup || form.controls[key] instanceof FormArray) {
          this.markAllAsTouched(<FormGroup>form.controls[key]);
        }
        form.controls[key].markAsTouched();
      });
      return;
    }

    form.markAsTouched();
  }

  static markAllAsDirty(form: UntypedFormGroup | FormGroup | UntypedFormArray | FormArray | FormControl) {
    if (form instanceof FormArray) {
      form.controls.forEach((control) => {
        if (control instanceof FormGroup || control instanceof FormArray) {
          this.markAllAsDirty(control);
        }
        control.markAsDirty();
      });
      return;
    } else if (form instanceof FormGroup) {
      Object.keys(form.controls).forEach((key) => {
        if (form.controls[key] instanceof FormGroup || form.controls[key] instanceof FormArray) {
          this.markAllAsDirty(<FormGroup>form.controls[key]);
        }
        form.controls[key].markAsDirty();
      });
      return;
    }

    form.markAsDirty();
  }

  static clearOrInitializeFormArray(formArray: FormArray) {
    if (formArray === null) {
      formArray = new FormArray<any>([]);
    }
    while (formArray.length !== 0) {
      formArray.removeAt(0);
    }
  }

  static scrollTo(el: Element) {
    if (el) {
      el.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  static scrollToError(): void {
    const firstElementWithError = document?.querySelector('.ng-invalid');
    if (firstElementWithError) {
      this.scrollTo(firstElementWithError);
    }
  }

  /**
   * Triggers change detection for OnPush components by dispatching a synthetic click event.
   * This is a workaround for when markAllAsTouched/markAllAsDirty doesn't trigger
   * change detection in OnPush child components.
   */
  static triggerChangeDetection(): void {
    setTimeout(() => {
      document.body.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    }, 100);
  }

  static scrollTop() {
    window.scrollTo(0, 0);
  }

  static removeEmpty(obj: any) {
    Object.keys(obj).forEach((key) => {
      if (obj[key] && typeof obj[key] === 'object') {
        this.removeEmpty(obj[key]);
      } else if (obj[key] == null) {
        delete obj[key];
      }
    });
  }

  static flattenErrorMessages(obj: any, prefix = '', res: { [key: string]: string[] } = {}) {
    if (Array.isArray(obj)) {
      for (let i = 0; i < obj.length; i++) {
        FormUtils.flattenErrorMessages(obj[i], prefix, res);
      }
    } else if (typeof obj === 'object' && obj !== null) {
      for (const key in obj) {
        const newPrefix = key === '*' ? `${prefix}.${key}` : prefix ? `${prefix}.${key}` : key;
        FormUtils.flattenErrorMessages(obj[key], newPrefix, res);
      }
    } else {
      if (!res[prefix]) {
        res[prefix] = [];
      }
      res[prefix].push(obj);
    }
    return res;
  }
}
